import { RoundButton } from '@/components/buttons/Buttons';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { Chip } from 'primereact/chip';
import type { DialogProps } from 'primereact/dialog';
import { FileUpload, type FileUploadHandlerEvent } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { classNames } from 'primereact/utils';
import { useState } from 'react';
import SecondaryModal from './SecondaryModal';
import './travel-customization.styles.css';

interface ModalProps extends DialogProps {
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const UploadExpenseCodeModal = (props: ModalProps) => {
	const { setVisible, ...rest } = props;
	const [selectedFile, setSelectedFile] = useState<File>();

	const isLoading = false;

	const onFileSelect = (event: FileUploadHandlerEvent) => {
		const file = event.files[0];
		setSelectedFile(file);
	};

	const handleUpload = () => {
		const notificationMessage = 'Your expense code list has been uploaded.';
		setVisible(false);
		showNotification(notificationMessage);
	};

	const headerContent = (
		<h1 className="tc-modal-header-title">Upload expense codes</h1>
	);

	const footerContent = (
		<div>
			<RoundButton
				label="Cancel"
				onClick={() => setVisible(false)}
				className="tc-text-button"
				id="cancel-upload-exp-code"
			/>
			<RoundButton
				label="Upload"
				onClick={handleUpload}
				id="upload-exp-code"
			/>
		</div>
	);

	const loadingTemplate = (
		<div className="mt-5 flex flex-col items-center gap-4 border-2 border-dashed border-blue-400 rounded-lg p-6 text-center">
			<ProgressBar
				pt={{
					value: {
						className: classNames('tc-progress-bar'),
					},
				}}
				className="w-4/6"
				mode="indeterminate"
			/>
			<h1 className="text-sm text-body-small text-secondaryFixedDim">
				20% Complete
			</h1>
			<RoundButton
				className="tc-text-button tc-cancel-text"
				label="Cancel"
				id="cancel-upload-file"
			/>
		</div>
	);

	return (
		<SecondaryModal
			{...rest}
			draggable={false}
			header={headerContent}
			footer={footerContent}
			closable={false}
			style={{ width: '40rem' }}
		>
			<div className="font-poppins">
				<p className="text-base text-onSurfaceVariant">
					Uploading a new CSV file will delete and replace all
					existing expense codes and descriptions.
				</p>
				{isLoading && loadingTemplate}
				{selectedFile ? (
					<Chip
						pt={{
							root: {
								className: classNames('tc-chip'),
							},
						}}
						label={selectedFile.name}
						removable
						className="mt-5"
						onRemove={() => setSelectedFile(undefined)}
						removeIcon={'pi pi-times'}
					/>
				) : (
					<div className="mt-4 tc-upload-box p-5 text-center">
						<p className="text-xs text-body-small text-secondaryFixedDim mb-2">
							Drag and drop your .csv here or choose from your
							computer.
						</p>
						<p className="text-xs text-body-small text-secondaryFixedDim mb-4">
							Column format: Expense code, description,
							individual's email (optional).
						</p>
						<FileUpload
							mode="basic"
							name="csv"
							accept=".csv"
							maxFileSize={1000000}
							customUpload
							multiple={false}
							auto
							uploadHandler={onFileSelect}
							chooseOptions={{
								label: 'Browse CSV file',
								icon: <i />,
							}}
							pt={{
								basicButton: {
									className: classNames(
										'secondary tc-upload-button',
									),
								},
							}}
						/>
					</div>
				)}
				<p className="text-sm mt-4 text-onSurfaceVariant">
					Having trouble?
					<a href="/" className="text-accent60 underline ml-1">
						Download our template
					</a>
				</p>
			</div>
		</SecondaryModal>
	);
};

export default UploadExpenseCodeModal;
